/**
 * QuizContainer component
 *
 * Description:
 * Manages the quiz flow, including instructions, questions, feedback, and loading states. Handles data fetching and navigation between quiz steps.
 *
 * Features:
 * - Fetches quiz and evaluable data on mount.
 * - Displays instructions, questions, and feedback views.
 * - Handles navigation between views and quiz completion.
 * - Shows loading state while fetching data.
 * - Uses Material-UI for UI elements.
 */
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { getEvaluableStart, getQuiz } from 'store/lesson-actions';
import { getUserAditionalTrackCourse } from 'store/course-actions';

import Feedback from './Questions/Feedback/Feedback';
import Loading from 'components/UI/Loading/Loading';
import Questions from './Questions/Questions';
import QuizInstructions from './QuizInstructions/QuizInstructions';

let loadQuizData = false;

const QuizContainer = props => {
  const dispatch = useDispatch();
  // Local state for view management and quiz data
  const [questionsView, setQuestionsView] = useState(false);
  const [feedbackView, setFeedbackView] = useState(false);
  const [feedbackData, setFeedbackData] = useState('');
  const [calificationData, setCalificationData] = useState('');
  const [evaluate, setEvaluate] = useState(0);
  const [loading, seLoading] = useState(true);
  const dataLesson = useSelector(state => state.lessonInfo);
  const dataAcademy = useSelector(state => state.academyInfo);

  useEffect(() => {
    dispatch(
      getQuiz(dataLesson.data.idQuiz, 0, dataAcademy.data.shortName)
    );
    dispatch(
      getEvaluableStart(
        dataLesson.data.idQuiz,
        props.idCourse,
        0,
        dataAcademy.data.shortName
      )
    );
  }, [dataLesson.data.idQuiz, dataAcademy.data]);

  useEffect(() => {
    if (
      dataLesson.quizEvaluableData &&
      dataLesson.quizEvaluableData.idApplication > 0 &&
      dataLesson.data &&
      !loadQuizData
    ) {
      dispatch(
        getQuiz(
          dataLesson.data.idQuiz,
          dataLesson.quizEvaluableData.idApplication,
          dataAcademy.data.shortName
        )
      );
      dispatch(
        getEvaluableStart(
          dataLesson.data.idQuiz,
          props.idCourse,
          dataLesson.quizEvaluableData.idApplication,
          dataAcademy.data.shortName
        )
      );
      loadQuizData = true;
    }
  }, [dataLesson.quizEvaluableData, dataLesson.data]);

  useEffect(() => {
    if (dataLesson.quizData && dataLesson.quizEvaluableData) {
      seLoading(false);
    }
  }, [dataLesson]);

  /**
   * Handles navigation between instructions, questions, and feedback views.
   */
  const changeViewHandler = () => {
    setQuestionsView(!questionsView);
    setFeedbackView(false);
  };

  /**
   * Handles showing feedback after quiz completion.
   */
  const showFeedbackHandler = (
    feedbackData,
    calification,
    evaluate
  ) => {
    setEvaluate(evaluate);
    setFeedbackData(feedbackData);
    setCalificationData(calification);
    setFeedbackView(true);
    dispatch(getUserAditionalTrackCourse(props.idCourse));
  };

  return (
    <>
      {!feedbackView ? (
        <>
          {!loading && (
            <>
              {!questionsView ? (
                <QuizInstructions
                  // Custom prop: 'description' passes quiz description
                  description={dataLesson.quizData.description}
                  // Custom prop: 'preCalifications' passes previous califications
                  preCalifications={
                    dataLesson.quizEvaluableData.appsPrev
                  }
                  // Custom prop: 'attempts' passes total allowed attempts
                  attempts={dataLesson.quizData.attempt}
                  // Custom prop: 'changeView' passes handler for navigation
                  changeView={changeViewHandler}
                  time={Math.round(dataLesson.quizData.time / 60)}
                />
              ) : (
                <Questions
                  // Custom prop: 'idQuiz' passes quiz ID
                  idQuiz={dataLesson.quizData.idQuiz}
                  // Custom prop: 'idApplication' passes application ID
                  idApplication={
                    dataLesson.quizEvaluableData.idApplication
                  }
                  // Custom prop: 'changeView' passes handler for navigation
                  changeView={changeViewHandler}
                  // Custom prop: 'questions' passes questions array
                  questions={dataLesson.quizData.questions}
                  // Custom prop: 'showFeedback' passes handler for feedback
                  showFeedback={showFeedbackHandler}
                  // Custom prop: 'changeQuizStatus' passes handler for quiz status
                  changeQuizStatus={props.changeQuizStatus}
                  // Custom prop: 'time' passes quiz limit time in seconds
                  time={dataLesson.quizData.time}
                />
              )}
            </>
          )}
          {loading && <Loading />}
        </>
      ) : (
        <Feedback
          // Custom prop: 'feedbackData' passes feedback info
          feedbackData={feedbackData}
          // Custom prop: 'calificationData' passes calification info
          calificationData={calificationData}
          // Custom prop: 'evaluate' passes evaluation score
          evaluate={evaluate}
          // Custom prop: 'changeView' passes handler for navigation
          changeView={changeViewHandler}
          // Custom prop: 'returnVideo' passes handler for retry
          returnVideo={props.returnVideo}
        />
      )}
    </>
  );
};

QuizContainer.propTypes = {
  /**
   * Course ID for quiz data fetching
   */
  idCourse: PropTypes.number,
  /**
   * Function to change quiz status
   */
  changeQuizStatus: PropTypes.func,
  /**
   * Function to handle returning to the video/quiz
   */
  returnVideo: PropTypes.func
};

QuizContainer.defaultProps = {
  idCourse: undefined,
  changeQuizStatus: undefined,
  returnVideo: undefined
};

export default QuizContainer;
